import { type ReactNode, useEffect, useState } from "react";
import { useStepperStore } from "@/store/stepper";
import type { StepperGraph } from "./types";
import { useFunnelStore } from "@/store";
import { queryClient } from "@/app/query-client";
import { devShowKeyNext, devMissionNo, setDevMissionNo } from "./dev-mission";
import { apiClient } from "@/shared/lib/api-client";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";
import { comebackLoopEnabled } from "./steptypes/comeback-config";
import { ComebackInbox } from "./comeback-inbox";

/**
 * Loads the stepper graph once on boot and holds the screens back until it's
 * ready. With the local fixture this resolves on the first tick, so the
 * "establishing" state is effectively invisible — it earns its keep when the
 * loader is swapped for the live `stepper-adventure` fetch.
 */
export function StepperGate({ children }: { children: ReactNode }) {
  const status = useStepperStore((s) => s.status);
  const load = useStepperStore((s) => s.load);

  useEffect(() => {
    load();
  }, [load]);

  if (status === "error") {
    return (
      <div className="grid h-[100dvh] place-content-center gap-4 text-center font-mono text-[11px] tracking-[0.22em] text-muted-foreground uppercase">
        <p>The line dropped.</p>
        <button
          type="button"
          onClick={() => void load()}
          className="mx-auto cursor-pointer rounded-md border border-current px-4 py-2 tracking-[0.18em]"
        >
          Try again
        </button>
      </div>
    );
  }
  if (status !== "ready") {
    return (
      <p className="text-center font-mono text-[11px] tracking-[0.22em] text-muted-foreground uppercase">
        Establishing secure line…
      </p>
    );
  }
  return (
    <>
      {children}
      {import.meta.env.DEV || import.meta.env.VITE_DEV_JUMP === "true" ? (
        <DevJump />
      ) : null}
      {comebackLoopEnabled() ? <ComebackInbox /> : null}
    </>
  );
}

/** The steps a walk from `start` can reach, in the graph's order. */
function reachableFrom(graph: StepperGraph, start: string) {
  const seen = new Set([start]);
  const queue = [start];
  while (queue.length) {
    for (const b of graph.steps[queue.shift()!]?.branches ?? [])
      if (graph.steps[b.nextStep] && !seen.has(b.nextStep)) {
        seen.add(b.nextStep);
        queue.push(b.nextStep);
      }
  }
  return Object.values(graph.steps).filter((s) => seen.has(s.stepName));
}

/**
 * Dev only: jump anywhere. Every step of the adventure, a mission number
 * (1–14, so the vault lock and handler unlock can be seen without filing that
 * many debriefs — see dev-mission.ts), and a full restart as a brand-new
 * visitor. The session cookie is httponly, so the server swaps it (`fresh`);
 * everything else the app keeps lives in localStorage.
 */
function DevJump() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const graph = useStepperStore((s) => s.graph);
  const current = useStepperStore((s) => s.currentStepName);
  const status = useStepperStore((s) => s.status);
  const goTo = useStepperStore((s) => s.goTo);
  const [missionNo, setMissionNo] = useState(() => devMissionNo() ?? 0);

  const restart = async () => {
    setBusy(true);
    try {
      await apiClient.post(API_ENDPOINTS.session.ensure, { fresh: true });
    } catch {
      /* mocks / no backend — the local reset below is still worth doing */
    }
    localStorage.clear();
    window.location.assign("/");
  };
  const jump = (step: string) => {
    // the mission screens need a mission: give them one if none was accepted
    const f = useFunnelStore.getState();
    if (!f.missionId)
      f.acceptMission({ tier: f.tier ?? "beginner", missionId: 1 });
    goTo(step);
    setOpen(false);
  };
  const pickMission = (n: number) => {
    setMissionNo(n);
    setDevMissionNo(n || null);
    void queryClient.invalidateQueries();
  };

  // (in sequence order: the server sends them that way). Past the first
  // mission a run starts at the doors, so only what is reachable from there.
  const all = graph ? Object.values(graph.steps) : [];
  // the key is engraved over the debrief once it is filed: not a step
  const debrief = all.find((s) => s.stepType === "WebSmDebrief");
  const steps =
    graph && missionNo > 1 && graph.steps.Doors
      ? reachableFrom(graph, "Doors")
      : all;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-3 left-3 z-[100] cursor-pointer rounded-md border border-white/25 bg-black/70 px-2.5 py-1 font-mono text-[10px] tracking-[.14em] text-white/80 uppercase hover:bg-black/90"
      >
        Dev jump
      </button>
      {open ? (
        <div className="fixed bottom-11 left-3 z-[100] max-h-[70dvh] w-[230px] overflow-y-auto rounded-lg border border-white/20 bg-black/90 p-2 font-mono text-[11px] text-white/85 shadow-[0_10px_30px_rgba(0,0,0,.6)]">
          <label className="flex items-center justify-between gap-2 px-1 py-1">
            <span className="tracking-[.12em] uppercase">Mission #</span>
            <select
              value={missionNo}
              onChange={(e) => pickMission(Number(e.target.value))}
              className="rounded border border-white/25 bg-black px-1 py-0.5"
            >
              <option value={0}>real</option>
              {Array.from({ length: 14 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <div className="my-1 h-px bg-white/15" />
          {steps.length === 0 ? (
            // say why, rather than a blank list
            <div className="px-2 py-1 text-white/50">
              {graph
                ? "No steps in this adventure."
                : `Steps not loaded (${status}). Reload the page.`}
            </div>
          ) : null}
          {steps.map((s) => (
            <button
              key={s.stepName}
              type="button"
              onClick={() => jump(s.stepName)}
              className={`block w-full cursor-pointer rounded px-2 py-1 text-left hover:bg-white/10 ${
                s.stepName === current ? "bg-white/15 text-white" : ""
              }`}
            >
              {s.stepName}
              <span className="ml-1 text-white/40">{s.stepType}</span>
            </button>
          ))}
          {debrief ? (
            <button
              type="button"
              onClick={() => {
                devShowKeyNext();
                jump(debrief.stepName);
              }}
              className="block w-full cursor-pointer rounded px-2 py-1 text-left hover:bg-white/10"
            >
              Key engraving
              <span className="ml-1 text-white/40">
                over {debrief.stepName}
              </span>
            </button>
          ) : null}
          <div className="my-1 h-px bg-white/15" />
          <button
            type="button"
            onClick={restart}
            disabled={busy}
            className="block w-full cursor-pointer rounded px-2 py-1 text-left text-[#ff9b8f] hover:bg-white/10 disabled:cursor-wait"
          >
            {busy ? "Restarting…" : "Restart as a new visitor"}
          </button>
        </div>
      ) : null}
    </>
  );
}
